/**
 * Enterprise Backup Service
 * Handles all backup creation, encryption, storage, and retrieval
 */

import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  setDoc, 
  updateDoc,
  deleteDoc,
  orderBy,
  limit
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject, getBlob } from 'firebase/storage';
import { db, storage } from '../firebase/client';
import { 
  BackupSettings, 
  BackupHistoryRecord, 
  BackupManifest,
  ExportOptions,
  BackupType,
  BackupPhotoMetadata,
  BackupVoiceMemoMetadata,
  BackupNoteMetadata
} from '../../types/backup';
import JSZip from 'jszip';
import CryptoJS from 'crypto-js';

class BackupService {
  /**
   * Get backup settings for a company
   */
  async getBackupSettings(companyId: string): Promise<BackupSettings | null> {
    try {
      const settingsRef = doc(db, `companies/${companyId}/settings/backup`);
      const settingsDoc = await getDocs(query(collection(db, `companies/${companyId}/settings`), where('__name__', '==', 'backup')));
      
      if (settingsDoc.empty) {
        return null;
      }

      return settingsDoc.docs[0].data() as BackupSettings;
    } catch (error) {
      console.error('Error fetching backup settings:', error);
      throw error;
    }
  }

  /**
   * Save backup settings
   */
  async saveBackupSettings(companyId: string, settings: BackupSettings): Promise<void> {
    try {
      const settingsRef = doc(db, `companies/${companyId}/settings/backup`);
      await setDoc(settingsRef, settings, { merge: true });
    } catch (error) {
      console.error('Error saving backup settings:', error);
      throw error;
    }
  }

  /**
   * Get backup history for a company
   */
  async getBackupHistory(companyId: string): Promise<BackupHistoryRecord[]> {
    try {
      const historyRef = collection(db, `companies/${companyId}/backups/history`);
      const q = query(historyRef, orderBy('startedAt', 'desc'), limit(20));
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BackupHistoryRecord[];
    } catch (error) {
      console.error('Error fetching backup history:', error);
      return [];
    }
  }

  /**
   * Calculate storage usage
   */
  async calculateStorageUsage(companyId: string): Promise<{ used: number; total: number }> {
    try {
      const history = await this.getBackupHistory(companyId);
      const used = history.reduce((sum, backup) => sum + backup.manifest.totalSize, 0);
      
      return {
        used,
        total: 10 * 1024 * 1024 * 1024 // 10 GB limit (can be dynamic)
      };
    } catch (error) {
      console.error('Error calculating storage:', error);
      return { used: 0, total: 0 };
    }
  }

  /**
   * Create a manual backup
   */
  async createManualBackup(companyId: string, user: any, settings: Partial<BackupSettings>): Promise<{ size: number; id: string }> {
    
    try {
      const backupId = `backup-${Date.now()}`;
      const startTime = Date.now();

      // Collect all data
      const data = await this.collectAllData(companyId, settings);

      // Create manifest
      const manifest: BackupManifest = {
        version: '1.0',
        schemaVersion: '2025.1',
        exportDate: new Date().toISOString(),
        exportType: 'manual',
        companyId,
        companyName: user.company || 'Unknown',
        dataRange: {
          from: data.dateRange.from,
          to: data.dateRange.to
        },
        recordCounts: data.recordCounts,
        files: {
          manifest: 'manifest.json',
          wells: 'wells.json',
          production: 'production.json',
          photos: 'photos.json',
          voiceMemos: 'voice-memos.json',
          notes: 'notes.json',
          tickets: 'tickets.json',
          inventory: 'inventory.json',
          financial: 'financial.json',
          users: 'users.json',
          analytics: 'analytics.json'
        },
        encryption: {
          enabled: settings.encryption?.enabled || false,
          algorithm: settings.encryption?.enabled ? 'AES-256-GCM' : undefined,
          keyId: settings.encryption?.masterKeyId
        },
        checksum: {
          algorithm: 'SHA-256',
          value: '' // Will be calculated after ZIP creation
        },
        totalSize: 0,
        compressedSize: 0,
        compressionRatio: 0,
        exportedBy: user.id,
        exportedByName: user.name,
        exportDuration: 0
      };

      // Create ZIP file
      const zip = new JSZip();
      zip.file('manifest.json', JSON.stringify(manifest, null, 2));
      zip.file('wells.json', JSON.stringify(data.wells, null, 2));
      zip.file('production.json', JSON.stringify(data.production, null, 2));
      zip.file('photos.json', JSON.stringify(data.photos, null, 2));
      zip.file('voice-memos.json', JSON.stringify(data.voiceMemos, null, 2));
      zip.file('notes.json', JSON.stringify(data.notes, null, 2));
      zip.file('tickets.json', JSON.stringify(data.tickets, null, 2));
      zip.file('inventory.json', JSON.stringify(data.inventory, null, 2));
      zip.file('financial.json', JSON.stringify(data.financial, null, 2));
      zip.file('users.json', JSON.stringify(data.users, null, 2));
      zip.file('analytics.json', JSON.stringify(data.analytics, null, 2));

      // Generate ZIP
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 9 }
      });

      // Encrypt if enabled
      let finalBlob = zipBlob;
      if (settings.encryption?.enabled) {
        finalBlob = await this.encryptBlob(zipBlob, settings.encryption.masterKeyId || 'default-key');
      }

      // Upload to Firebase Storage
      const storagePath = `backups/${companyId}/manual/${backupId}.zip`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, finalBlob);

      // Get download URL
      const downloadUrl = await getDownloadURL(storageRef);

      // Update manifest with final stats
      manifest.totalSize = zipBlob.size;
      manifest.compressedSize = finalBlob.size;
      manifest.compressionRatio = ((1 - finalBlob.size / zipBlob.size) * 100);
      manifest.exportDuration = (Date.now() - startTime) / 1000;
      manifest.checksum.value = await this.calculateChecksum(finalBlob);

      // Save backup history
      const historyRecord: BackupHistoryRecord = {
        id: backupId,
        type: 'manual',
        status: 'success',
        startedAt: new Date(startTime).toISOString(),
        completedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(), // 90 days
        storageUrl: storagePath,
        downloadUrl,
        downloadUrlExpiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        manifest,
        downloadCount: 0,
        verified: false,
        createdBy: user.id,
        createdByName: user.name,
        triggeredBy: 'manual'
      };

      const historyRef = doc(db, `companies/${companyId}/backups/history/${backupId}`);
      await setDoc(historyRef, historyRecord);


      return {
        size: finalBlob.size,
        id: backupId
      };
    } catch (error) {
      console.error('❌ Error creating manual backup:', error);
      throw error;
    }
  }

  /**
   * Export data to downloadable file
   */
  async exportData(companyId: string, user: any, options: ExportOptions): Promise<Blob> {

    try {
      // Collect data based on options
      const data = await this.collectAllData(companyId, {
        includePhotos: options.includePhotos,
        includeVoiceMemos: options.includeVoiceMemos,
        includeNotes: options.includeNotes,
        includeWells: options.includeWells,
        includeProduction: options.includeProduction,
        includeTickets: options.includeTickets,
        includeInventory: options.includeInventory,
        includeFinancial: options.includeFinancial,
        includeUsers: options.includeUsers,
        includeAnalytics: options.includeAnalytics
      });

      // Create ZIP
      const zip = new JSZip();
      
      const manifest = {
        exportDate: new Date().toISOString(),
        exportedBy: user.name,
        companyName: user.company,
        recordCounts: data.recordCounts
      };

      zip.file('manifest.json', JSON.stringify(manifest, null, 2));
      
      if (options.includeWells) zip.file('wells.json', JSON.stringify(data.wells, null, 2));
      if (options.includeProduction) zip.file('production.json', JSON.stringify(data.production, null, 2));
      if (options.includePhotos) zip.file('photos.json', JSON.stringify(data.photos, null, 2));
      if (options.includeVoiceMemos) zip.file('voice-memos.json', JSON.stringify(data.voiceMemos, null, 2));
      if (options.includeNotes) zip.file('notes.json', JSON.stringify(data.notes, null, 2));
      if (options.includeTickets) zip.file('tickets.json', JSON.stringify(data.tickets, null, 2));
      if (options.includeInventory) zip.file('inventory.json', JSON.stringify(data.inventory, null, 2));
      if (options.includeFinancial) zip.file('financial.json', JSON.stringify(data.financial, null, 2));
      if (options.includeUsers) zip.file('users.json', JSON.stringify(data.users, null, 2));
      if (options.includeAnalytics) zip.file('analytics.json', JSON.stringify(data.analytics, null, 2));

      const blob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 9 }
      });

      // Encrypt if requested
      if (options.encrypt) {
        return await this.encryptBlob(blob, 'export-key');
      }

      return blob;
    } catch (error) {
      console.error('Error exporting data:', error);
      throw error;
    }
  }

  /**
   * Get download URL for a backup
   */
  async getDownloadUrl(backupId: string, companyId: string): Promise<string> {
    try {
      const historyRef = doc(db, `companies/${companyId}/backups/history/${backupId}`);
      const historyDoc = await getDocs(query(collection(db, `companies/${companyId}/backups/history`), where('__name__', '==', backupId)));
      
      if (historyDoc.empty) {
        throw new Error('Backup not found');
      }

      const backup = historyDoc.docs[0].data() as BackupHistoryRecord;

      // Generate new signed URL if expired
      const storageRef = ref(storage, backup.storageUrl);
      const downloadUrl = await getDownloadURL(storageRef);

      // Update download count
      await updateDoc(historyRef, {
        downloadCount: (backup.downloadCount || 0) + 1,
        lastDownloadedAt: new Date().toISOString(),
        downloadUrl
      });

      return downloadUrl;
    } catch (error) {
      console.error('Error getting download URL:', error);
      throw error;
    }
  }

  /**
   * Delete a backup
   */
  async deleteBackup(backupId: string, companyId: string): Promise<void> {
    try {
      // Get backup record
      const historyRef = doc(db, `companies/${companyId}/backups/history/${backupId}`);
      const historyDoc = await getDocs(query(collection(db, `companies/${companyId}/backups/history`), where('__name__', '==', backupId)));
      
      if (historyDoc.empty) {
        throw new Error('Backup not found');
      }

      const backup = historyDoc.docs[0].data() as BackupHistoryRecord;

      // Delete from Firebase Storage
      const storageRef = ref(storage, backup.storageUrl);
      await deleteObject(storageRef);

      // Delete Firestore record
      await deleteDoc(historyRef);

    } catch (error) {
      console.error('Error deleting backup:', error);
      throw error;
    }
  }

  /**
   * Collect all data from Firestore
   */
  private async collectAllData(companyId: string, options: Partial<BackupSettings>): Promise<any> {
    const data: any = {
      wells: [],
      production: [],
      photos: [],
      voiceMemos: [],
      notes: [],
      tickets: [],
      inventory: [],
      financial: [],
      users: [],
      analytics: {},
      recordCounts: {
        wells: 0,
        productionEntries: 0,
        photos: 0,
        voiceMemos: 0,
        notes: 0,
        tickets: 0,
        inventoryItems: 0,
        financialRecords: 0,
        users: 0
      },
      dateRange: {
        from: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
        to: new Date().toISOString()
      }
    };

    try {
      // Wells
      if (options.includeWells !== false) {
        const wellsSnapshot = await getDocs(collection(db, `companies/${companyId}/wells`));
        data.wells = wellsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        data.recordCounts.wells = data.wells.length;
      }

      // Production Data
      if (options.includeProduction !== false) {
        const productionSnapshot = await getDocs(collection(db, `companies/${companyId}/production`));
        data.production = productionSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        data.recordCounts.productionEntries = data.production.length;
      }

      // Photos (metadata only)
      if (options.includePhotos) {
        const photosSnapshot = await getDocs(collection(db, `companies/${companyId}/photos`));
        data.photos = photosSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        data.recordCounts.photos = data.photos.length;
      }

      // Voice Memos (metadata only)
      if (options.includeVoiceMemos) {
        const voiceSnapshot = await getDocs(collection(db, `companies/${companyId}/voiceMemos`));
        data.voiceMemos = voiceSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        data.recordCounts.voiceMemos = data.voiceMemos.length;
      }

      // Notes
      if (options.includeNotes) {
        const notesSnapshot = await getDocs(collection(db, `companies/${companyId}/notes`));
        data.notes = notesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        data.recordCounts.notes = data.notes.length;
      }

      // Tickets
      if (options.includeTickets !== false) {
        const ticketsSnapshot = await getDocs(collection(db, `companies/${companyId}/tickets`));
        data.tickets = ticketsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        data.recordCounts.tickets = data.tickets.length;
      }

      // Inventory
      if (options.includeInventory !== false) {
        const inventorySnapshot = await getDocs(collection(db, `companies/${companyId}/inventory`));
        data.inventory = inventorySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        data.recordCounts.inventoryItems = data.inventory.length;
      }

      // Financial
      if (options.includeFinancial !== false) {
        const financialSnapshot = await getDocs(collection(db, `companies/${companyId}/invoices`));
        data.financial = financialSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        data.recordCounts.financialRecords = data.financial.length;
      }

      // Users (sanitized - no passwords)
      // Try tenant-scoped collection first, then fall back to legacy /users
      if (options.includeUsers !== false) {
        let usersSnapshot = await getDocs(collection(db, `companies/${companyId}/users`));
        
        // If no users in tenant-scoped, try legacy flat collection
        if (usersSnapshot.docs.length === 0) {
          usersSnapshot = await getDocs(query(collection(db, 'users'), where('companyId', '==', companyId)));
        }
        
        data.users = usersSnapshot.docs.map(doc => {
          const userData = doc.data();
          delete userData.password;
          delete userData.security?.pin;
          return { id: doc.id, ...userData };
        });
        data.recordCounts.users = data.users.length;
      }

      // Analytics (summary data)
      if (options.includeAnalytics !== false) {
        data.analytics = {
          totalWells: data.wells.length,
          totalProduction: data.production.length,
          averageDailyProduction: this.calculateAverageProduction(data.production),
          generatedAt: new Date().toISOString()
        };
      }

      return data;
    } catch (error) {
      console.error('Error collecting data:', error);
      throw error;
    }
  }

  /**
   * Encrypt blob with AES-256
   */
  private async encryptBlob(blob: Blob, key: string): Promise<Blob> {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer as any);
      const encrypted = CryptoJS.AES.encrypt(wordArray.toString(), key).toString();
      return new Blob([encrypted], { type: 'application/octet-stream' });
    } catch (error) {
      console.error('Encryption error:', error);
      throw error;
    }
  }

  /**
   * Calculate SHA-256 checksum
   */
  private async calculateChecksum(blob: Blob): Promise<string> {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      console.error('Checksum calculation error:', error);
      return 'unavailable';
    }
  }

  /**
   * Calculate average production
   */
  private calculateAverageProduction(production: any[]): number {
    if (production.length === 0) return 0;
    const total = production.reduce((sum, entry) => sum + (entry.oil_volume_bbl || 0), 0);
    return total / production.length;
  }
}

export const backupService = new BackupService();
